import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { marketplaceAPI, paymentsAPI } from "@/lib/api";
import { money } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Lock, Truck, MapPin, ShieldCheck } from "lucide-react";

function PayForm({ paymentId, amount, currency, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (error) {
      toast.error(error.message || "Payment failed");
      setProcessing(false);
      return;
    }
    if (paymentIntent && ["succeeded", "processing", "requires_capture"].includes(paymentIntent.status)) {
      try {
        const res = await paymentsAPI.confirmPayment(paymentId);
        toast.success("Payment successful — funds held securely");
        onDone(res.data?.order_id);
      } catch {
        toast.error("Payment succeeded but order confirmation failed. Check My Orders.");
        onDone(null);
      }
    } else {
      toast.error("Payment not completed");
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement />
      <Button type="submit" disabled={!stripe || processing} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="checkout-pay-button">
        {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="mr-2 h-4 w-4" /> Pay {money(amount / 100, currency)}</>}
      </Button>
    </form>
  );
}

export default function Checkout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forcedPickup = searchParams.get("pickup") === "true";
  const shippingCostParam = parseFloat(searchParams.get("shipping_cost") || "0") || 0;
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState(null); // 'pickup' | 'ship'
  const [intent, setIntent] = useState(null);
  const [creating, setCreating] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    paymentsAPI.getConfig().then((res) => {
      if (res.data?.publishable_key) setStripePromise(loadStripe(res.data.publishable_key));
    });
  }, []);

  useEffect(() => {
    marketplaceAPI
      .getListing(id)
      .then((res) => {
        setListing(res.data);
        const canPickup = ["local_only", "both"].includes(res.data.shipping_option) || res.data.local_pickup_available;
        setMethod(forcedPickup && canPickup ? "pickup" : (canPickup && !["ship_only"].includes(res.data.shipping_option) ? "pickup" : "ship"));
      })
      .catch(() => toast.error("Listing not found"))
      .finally(() => setLoading(false));
  }, [id, forcedPickup]);

  const shippingCost = method === "pickup" ? 0 : shippingCostParam;

  const createIntent = async () => {
    setCreating(true);
    try {
      const res = await paymentsAPI.createPaymentIntent({
        listing_id: id,
        payment_type: "listing_purchase",
        is_local_pickup: method === "pickup",
        shipping_cost: shippingCost,
      });
      setIntent(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
    } finally {
      setCreating(false);
    }
  };

  const options = useMemo(
    () =>
      intent?.client_secret
        ? { clientSecret: intent.client_secret, appearance: { theme: "night", variables: { colorPrimary: "#e63946" } } }
        : null,
    [intent]
  );

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!listing) return null;

  const canPickup = ["local_only", "both"].includes(listing.shipping_option);
  const canShip = ["ship_only", "both"].includes(listing.shipping_option);
  const isMock = intent?.is_mock;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="font-display text-2xl font-bold">Checkout</h1>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* Payment */}
        <div className="space-y-5">
          {canPickup && canShip && (
            <Card className="border-border bg-card p-5">
              <h3 className="mb-3 font-semibold">Delivery method</h3>
              <RadioGroup value={method} onValueChange={(v) => { setMethod(v); setIntent(null); }} className="space-y-2">
                <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border p-3 hover:bg-secondary/40" data-testid="checkout-method-pickup">
                  <RadioGroupItem value="pickup" id="pickup" />
                  <MapPin className="h-4 w-4 text-primary" />
                  <span className="flex-1"><span className="font-medium">Local pickup</span><span className="block text-xs text-muted-foreground">{listing.location || "Arrange with seller"}</span></span>
                </label>
                <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border p-3 hover:bg-secondary/40" data-testid="checkout-method-ship">
                  <RadioGroupItem value="ship" id="ship" />
                  <Truck className="h-4 w-4 text-primary" />
                  <span className="flex-1"><span className="font-medium">Shipping</span><span className="block text-xs text-muted-foreground">Shipping details arranged with the seller after purchase</span></span>
                </label>
              </RadioGroup>
            </Card>
          )}

          <Card className="border-border bg-card p-5">
            <h3 className="mb-3 font-semibold">Payment</h3>
            {!intent ? (
              <Button onClick={createIntent} disabled={creating} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="checkout-continue-button">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to payment"}
              </Button>
            ) : isMock ? (
              <div className="space-y-4">
                <p className="rounded-md bg-secondary/40 p-3 text-sm text-muted-foreground">Test mode — no real card charged.</p>
                <Button
                  onClick={async () => {
                    const res = await paymentsAPI.confirmPayment(intent.payment_intent_id);
                    toast.success("Payment successful");
                    navigate("/orders");
                  }}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="checkout-pay-button"
                >
                  Pay {money(intent.amount / 100, listing.currency)}
                </Button>
              </div>
            ) : options && stripePromise ? (
              <Elements stripe={stripePromise} options={options}>
                <PayForm
                  paymentId={intent.payment_intent_id}
                  amount={intent.amount}
                  currency={listing.currency}
                  onDone={() => navigate("/orders")}
                />
              </Elements>
            ) : (
              <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            )}
          </Card>

          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Your payment is held securely and released to the seller once you confirm delivery.
          </p>

          <div className="rounded-lg border border-primary/30 bg-primary/[0.06] p-4" data-testid="checkout-fee-note">
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" /> Just a 3% service fee, nothing hidden
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
              Includes a 3% service fee, charged only to cover Stripe payment processing. All SRC services and buyer protection are included at no extra cost.
            </p>
          </div>
        </div>

        {/* Summary */}
        <Card className="h-fit border-border bg-card p-5">
          <h3 className="mb-3 font-semibold">Order summary</h3>
          <div className="flex gap-3">
            <div className="h-16 w-16 overflow-hidden rounded-md bg-secondary">
              {listing.images?.[0] && <img src={listing.images[0]} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="flex-1">
              <p className="font-medium leading-tight">{listing.title}</p>
              <p className="text-xs text-muted-foreground">Sold by {listing.seller_username}</p>
            </div>
          </div>
          <div className="mt-4 space-y-2 border-t border-border pt-4 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Item price</span><span>{money(listing.price, listing.currency)}</span></div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Shipping</span>
              <span>
                {method === "pickup"
                  ? "Free (pickup)"
                  : shippingCost > 0
                  ? money(shippingCost, listing.currency)
                  : "Arranged w/ seller"}
              </span>
            </div>
            <div className="flex justify-between border-t border-border pt-2 font-display text-lg font-bold">
              <span>Total</span>
              <span className="text-primary">{money((listing.price || 0) + (method === "pickup" ? 0 : shippingCost), listing.currency)}</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
